import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSocialAccounts } from '../hooks/useSocialAccounts';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { SocialPlatform, SocialMediaAccountPublic } from '@/lib/api';
import { ALL_PLATFORMS } from '../components/platform-constants';

const SOCIAL_PLATFORMS = ALL_PLATFORMS.filter((p): p is SocialPlatform => p !== 'tiktok');
import { toast } from '@/components/ui/use-toast';

const PLATFORM_CONFIG: Record<
  SocialPlatform,
  { label: string; descriptor: string; bgClass: string; initial: string }
> = {
  twitter: {
    label: 'X / Twitter',
    descriptor: 'Share short-form content and engage with your audience.',
    bgClass: 'bg-black',
    initial: 'X',
  },
  linkedin: {
    label: 'LinkedIn',
    descriptor: 'Reach professionals and grow your business network.',
    bgClass: 'bg-blue-700',
    initial: 'in',
  },
  facebook: {
    label: 'Facebook',
    descriptor: 'Connect with communities and share across your network.',
    bgClass: 'bg-blue-600',
    initial: 'f',
  },
  instagram: {
    label: 'Instagram',
    descriptor: 'Share visual content and grow a creative following.',
    bgClass: 'bg-gradient-to-br from-purple-500 to-pink-500',
    initial: 'IG',
  },
  tiktok: {
    label: 'TikTok',
    descriptor: 'Create and share short-form video content.',
    bgClass: 'bg-black',
    initial: 'TT',
  },
  youtube: {
    label: 'YouTube',
    descriptor: 'Publish video content and grow your channel audience.',
    bgClass: 'bg-red-600',
    initial: 'YT',
  },
  telegram: {
    label: 'Telegram',
    descriptor: 'Broadcast messages and engage your Telegram channel.',
    bgClass: 'bg-blue-400',
    initial: 'TG',
  },
};

function PlatformIcon({ platform, size = 'lg' }: { platform: SocialPlatform; size?: 'sm' | 'lg' }) {
  const config = PLATFORM_CONFIG[platform];
  const sizeClass = size === 'lg' ? 'w-14 h-14 text-base' : 'w-8 h-8 text-xs';
  return (
    <div
      className={`${config.bgClass} text-white ${sizeClass} rounded-full flex items-center justify-center font-bold shrink-0`}
    >
      {config.initial}
    </div>
  );
}

interface UnconnectedCardProps {
  platform: SocialPlatform;
  onConnect: (platform: SocialPlatform) => void;
}

function UnconnectedCard({ platform, onConnect }: UnconnectedCardProps) {
  const config = PLATFORM_CONFIG[platform];
  return (
    <div className="border-2 border-dashed rounded-xl p-6 bg-background shadow-social-card flex flex-col items-center text-center gap-3 min-h-[220px] justify-between">
      <div className="flex flex-col items-center gap-3">
        <PlatformIcon platform={platform} />
        <div className="flex flex-col items-center gap-2">
          <p className="font-bold text-sm">{config.label}</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-[75%]">{config.descriptor}</p>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onConnect(platform)}
        className="w-full font-mono uppercase !text-[12px]"
      >
        Connect Now
      </Button>
    </div>
  );
}

interface ConnectedCardProps {
  account: SocialMediaAccountPublic;
  onDisconnect: (accountId: string) => void;
}

function ConnectedCard({ account, onDisconnect }: ConnectedCardProps) {
  const config = PLATFORM_CONFIG[account.platform];
  const displayName = account.platformName ?? account.platformUsername ?? account.platform;

  return (
    <div className="border border-dashed rounded-xl p-6 bg-background shadow-social-card flex flex-col items-center text-center gap-3 min-h-[220px] justify-between">
      <div className="flex flex-col items-center gap-3">
        {/* Layered avatar: user profile peeking from left, platform icon in front on right */}
        <div className="relative w-[52px] h-10">
          <div className="absolute left-0 top-0 z-0">
            {account.platformAvatarUrl ? (
              <img
                src={account.platformAvatarUrl}
                alt={displayName}
                className="w-10 h-10 rounded-full border-2 border-white object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-muted border-2 border-white flex items-center justify-center text-muted-foreground text-sm font-bold">
                {displayName[0].toUpperCase()}
              </div>
            )}
          </div>
          <div className="absolute right-0 top-0 z-10">
            <PlatformIcon platform={account.platform} size="sm" />
          </div>
        </div>
        <div className="flex flex-col items-center gap-2">
          <p className="font-bold text-sm">{config.label}</p>
          {account.platformUsername && (
            <p className="text-[10px] text-muted-foreground/70 mt-0.5 font-mono">
              @{account.platformUsername}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1 max-w-[75%]">{config.descriptor}</p>
        </div>
      </div>
      <Button
        variant="outline"
        onClick={() => onDisconnect(account.accountId)}
        className="w-full text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600 font-mono uppercase !text-[12px]"
      >
        Disconnect
      </Button>
    </div>
  );
}

export function AccountsPage() {
  const { selectedHolderId } = useHolderSwitcher();
  const { accounts, isLoading, error, loadAccounts, connectAccount, disconnectAccount } =
    useSocialAccounts(selectedHolderId);
  const [searchParams, setSearchParams] = useSearchParams();

  const connected = searchParams.get('connected') === 'true';
  const connectError = searchParams.get('error');

  // Show toast and clear status params after OAuth callback
  useEffect(() => {
    if (connected) {
      toast({ title: 'Account connected successfully' });
      setSearchParams({});
      loadAccounts();
    } else if (connectError) {
      toast({ title: 'Connection failed', description: connectError, variant: 'destructive' });
      setSearchParams({});
      loadAccounts();
    }
  }, [connected, connectError, setSearchParams, loadAccounts]);

  const connectedAccountsByPlatform = new Map(accounts.map((a) => [a.platform, a]));

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <Skeleton className="h-7 w-36" />
            <Skeleton className="h-4 w-52" />
          </div>
          <Skeleton className="h-9 w-36" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-[220px] rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-social-bg h-full">
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold font-satoshi">All Channel</h1>
          <p className="text-sm text-muted-foreground mt-0.5 font-satoshi">
            Manage your channels here.
          </p>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">{error}</div>
      )}

      {/* 3-column responsive grid of platform cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {SOCIAL_PLATFORMS.map((platform) => {
          const account = connectedAccountsByPlatform.get(platform);
          return account ? (
            <ConnectedCard key={platform} account={account} onDisconnect={disconnectAccount} />
          ) : (
            <UnconnectedCard
              key={platform}
              platform={platform as SocialPlatform}
              onConnect={connectAccount}
            />
          );
        })}
      </div>
    </div>
  );
}
